import { useState } from "react";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Eye, EyeOff, Shield, Lock, Plus, MessageSquare, FileText, BarChart3, FolderOpen, Users } from "lucide-react";
import AddProjectModal from "@/components/admin/AddProjectModal";
import MessagesModal from "@/components/admin/MessagesModal";
import QuotesModal from "@/components/admin/QuotesModal";
import { hashPassword } from "@/utils/auth";
import ProjectsManagementModal from "@/components/admin/ProjectsManagementModal";
import TeamManagementModal from "@/components/admin/TeamManagementModal";

const MukeshAdminPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [showProjectsManagement, setShowProjectsManagement] = useState(false);
  const [showTeamManagement, setShowTeamManagement] = useState(false);
  const { toast } = useToast();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loginAttempts >= 5) {
      toast({
        variant: "destructive",
        title: "Too Many Attempts",
        description: "Please wait before trying again.",
      });
      return;
    }

    if (!username || !password) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please enter both username and password.",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Direct database query to admin_users table instead of using edge function
      console.log('Attempting admin login with username:', username);
      
      const { data: adminUser, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('username', username)
        .single();

      if (error || !adminUser) {
        console.error('Admin user not found:', error);
        throw new Error('Invalid credentials');
      }

      // Verify password_hash and salt exist
      if (!adminUser.password_hash || !adminUser.salt) {
        console.error('Missing password hash or salt');
        throw new Error('Invalid credentials');
      }

      // Hash the provided password with the stored salt using Web Crypto API (SHA-256)
      // This matches the hashing method used in the database and edge function
      const passwordHash = await hashPassword(password, adminUser.salt);

      // Compare the hashed password with the stored hash
      if (passwordHash === adminUser.password_hash) {
        console.log('Admin login successful');
        setIsAuthenticated(true);
        
        // Update last login
        await supabase
          .from('admin_users')
          .update({ last_login: new Date().toISOString() })
          .eq('id', adminUser.id);

        // Log successful login
        await supabase.from('admin_logs').insert({
          admin_id: adminUser.id,
          action: 'login_success',
          status: 'success',
          ip_address: window.location.hostname,
          user_agent: navigator.userAgent
        });

        toast({
          title: "Login Successful",
          description: `Welcome back, ${username}!`,
        });
      } else {
        console.error('Password verification failed');
        setLoginAttempts(prev => prev + 1);
        
        // Log failed attempt
        await supabase.from('admin_logs').insert({
          admin_id: adminUser.id,
          action: 'login_failed',
          status: 'failed',
          ip_address: window.location.hostname,
          user_agent: navigator.userAgent,
          attempted_username: username
        });

        toast({
          variant: "destructive",
          title: "Login Failed",
          description: "Invalid password. Please check your credentials.",
        });
      }

    } catch (error: any) {
      setLoginAttempts(prev => prev + 1);
      console.error('Admin login error:', error);
      
      // Log failed attempt
      await supabase.from('admin_logs').insert({
        action: 'login_failed',
        status: 'failed',
        ip_address: window.location.hostname,
        user_agent: navigator.userAgent,
        attempted_username: username,
        error_message: error.message
      });

      toast({
        variant: "destructive",
        title: "Login Failed",
        description: "Invalid username or password.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername("");
    setPassword("");
    setLoginAttempts(0);
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
    });
  };

  const handleProjectAdded = () => {
    toast({
      title: "Success",
      description: "Project has been added successfully!",
    });
  };

  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-semibold text-gray-900">
                Welcome back, {username}
              </h1>
              <button
                onClick={handleLogout}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 sm:px-0">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Admin Dashboard</h2>
              <p className="text-gray-600 mb-8">Welcome to the admin portal. You can manage the website from here.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <div className="flex items-center">
                    <FileText className="w-8 h-8 text-blue-600 mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-blue-900">Projects</h3>
                      <p className="text-blue-700 mt-1">Manage portfolio</p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-6 rounded-lg">
                  <div className="flex items-center">
                    <MessageSquare className="w-8 h-8 text-green-600 mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-green-900">Messages</h3>
                      <p className="text-green-700 mt-1">Contact forms</p>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 p-6 rounded-lg">
                  <div className="flex items-center">
                    <BarChart3 className="w-8 h-8 text-purple-600 mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-purple-900">Quotes</h3>
                      <p className="text-purple-700 mt-1">Quote requests</p>
                    </div>
                  </div>
                </div>
                <div className="bg-orange-50 p-6 rounded-lg">
                  <div className="flex items-center">
                    <FolderOpen className="w-8 h-8 text-orange-600 mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-orange-900">Manage</h3>
                      <p className="text-orange-700 mt-1">Edit projects</p>
                    </div>
                  </div>
                </div>
                <div className="bg-teal-50 p-6 rounded-lg">
                  <div className="flex items-center">
                    <Users className="w-8 h-8 text-teal-600 mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-teal-900">Team</h3>
                      <p className="text-teal-700 mt-1">Manage members</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <button 
                    onClick={() => setShowAddProject(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Project
                  </button>
                  <button 
                    onClick={() => setShowMessages(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center"
                  >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Messages
                  </button>
                  <button 
                    onClick={() => setShowQuotes(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center"
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    Quotes
                  </button>
                  <button 
                    onClick={() => setShowProjectsManagement(true)}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center"
                  >
                    <FolderOpen className="w-5 h-5 mr-2" />
                    Projects
                  </button>
                  <button 
                    onClick={() => setShowTeamManagement(true)}
                    className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center"
                  >
                    <Users className="w-5 h-5 mr-2" />
                    Team
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>

        <AddProjectModal 
          isOpen={showAddProject} 
          onClose={() => setShowAddProject(false)}
          onProjectAdded={handleProjectAdded}
        />
        <MessagesModal 
          isOpen={showMessages} 
          onClose={() => setShowMessages(false)}
        />
        <QuotesModal 
          isOpen={showQuotes} 
          onClose={() => setShowQuotes(false)}
        />
        <ProjectsManagementModal
          isOpen={showProjectsManagement}
          onClose={() => setShowProjectsManagement(false)}
        />

        <TeamManagementModal
          isOpen={showTeamManagement}
          onClose={() => setShowTeamManagement(false)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900">
      <div className="absolute inset-0 bg-black/20"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4"
            >
              <Shield className="w-8 h-8 text-white" />
            </motion.div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Portal</h1>
            <p className="text-gray-600">Secure administrative access</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter username"
                required
                autoComplete="username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Enter password"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {loginAttempts > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700 text-sm">
                  Failed attempts: {loginAttempts}/5
                </p>
              </div>
            )}

            <motion.button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center disabled:opacity-70"
              whileHover={{ scale: isLoading ? 1 : 1.02 }}
              whileTap={{ scale: isLoading ? 1 : 0.98 }}
              disabled={isLoading || loginAttempts >= 5}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Authenticating...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5 mr-2" />
                  Login
                </>
              )}
            </motion.button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              🔒 Secure admin portal .
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default MukeshAdminPage;